import { BLOCKS } from "@contentful/rich-text-types";
import { Box, Grid, Typography, useMediaQuery, useTheme } from "@mui/material";
import { useCallback } from "react";
import { routes } from "../../constants/routes";
import { MarketingClaim, Project, Solution } from "../../models/schema";
import { colors } from "../../theme/colors";
import { generateId } from "../../utils/utilities";
import { AnimatedView } from "../utils/AnimatedView";
import NavButton from "../utils/NavButton";
import RenderContent from "../utils/RenderContent";
import { ConnectionType, ProjectCard } from "./ProjectCard";

export const SolutionCard = ({ solution }: { solution: Solution }) => {
  const { primaryProject, secondaryProjectsCollection } = solution;

  const theme = useTheme();
  const lessThanMedium = useMediaQuery(theme.breakpoints.down("md"));
  const lessThanLarge = useMediaQuery(theme.breakpoints.down("lg"));

  const computeConnections = useCallback(
    (index: number): ConnectionType[] => {
      if (lessThanMedium) {
        return [ConnectionType.TOP]; // All items connect to the top on mobile
      }

      if (lessThanLarge) {
        if ((index + 1) % 2 === 1) {
          return [ConnectionType.START]; // First item in row connects to the parent
        } else {
          return [ConnectionType.LEFT]; // Second and last item in row connects to the left
        }
      }

      if ((index + 1) % 3 === 1) {
        return [ConnectionType.START]; // First item in row connects to the parent
      } else {
        return [ConnectionType.LEFT]; // Second and last item in row connects to the left
      }
    },
    [lessThanMedium, lessThanLarge]
  );

  const Image = ({ reversed }: { reversed?: boolean }) => {
    const clipPath = reversed
      ? "polygon(0 0, 100% 0, 100% 100%, 25% 100%);"
      : "polygon(0 0, 75% 0, 100% 100%, 0% 100%);";

    const margin = reversed ? "0 0 0 -5rem" : "0 -5rem 0 0";

    return (
      <Box
        sx={{
          width: { xs: "100%", md: "30%" },
          minHeight: { xs: "20rem", md: "100%" },
          background: `url(${primaryProject?.marketingImageXs?.url as string})`,
          backgroundSize: "cover",
          backgroundPosition: "bottom",
          backgroundRepeat: "no-repeat",
          backgroundColor: colors.white,
          clipPath: { xs: "none", md: clipPath },
          margin: { xs: "0", md: margin },
          borderTopLeftRadius: { xs: 10, md: reversed ? 0 : 10 },
          borderBottomLeftRadius: { xs: 0, md: reversed ? 0 : 10 },
          borderTopRightRadius: { xs: 10, md: reversed ? 10 : 0 },
          borderBottomRightRadius: { xs: 0, md: reversed ? 10 : 0 }
        }}
      />
    );
  };

  const Highlight = ({ reversed }: { reversed?: boolean }) => {
    const margin = reversed ? "0 -5rem 0 0" : "0 0 0 -5rem";
    const clipPath = reversed
      ? "polygon(0 0, 75% 0, 100% 100%, 0% 100%);"
      : "polygon(0 0, 100% 0, 100% 100%, 25% 100%);";
    const marketingClaim: MarketingClaim = primaryProject?.marketingClaimsCollection
      ?.items?.[0] as MarketingClaim;

    return (
      <Box
        sx={{
          width: { xs: "100%", md: "25%" },
          minHeight: { xs: "auto", md: "100%" },
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          backgroundColor: colors.grey5,
          p: 5,
          pr: {
            xs: 5,
            md: reversed ? 10 : 5
          },
          pl: {
            xs: 5,
            md: reversed ? 5 : 10
          },
          gap: 3,
          textAlign: "center",
          clipPath: { xs: "none", md: clipPath },
          margin: { xs: "0", md: margin },
          borderTopLeftRadius: { xs: 0, md: reversed ? 10 : 0 },
          borderBottomLeftRadius: { xs: 10, md: reversed ? 10 : 0 },
          borderTopRightRadius: { xs: 0, md: reversed ? 0 : 10 },
          borderBottomRightRadius: { xs: 10, md: reversed ? 0 : 10 }
        }}>
        <Typography sx={{ fontSize: { xs: "1.5rem", md: "2rem" } }} color={colors.yellow}>
          {marketingClaim?.number as string}
        </Typography>
        <RenderContent
          content={marketingClaim?.text?.json}
          options={{
            renderNode: {
              [BLOCKS.PARAGRAPH]: (node, children) => (
                <Typography color={colors.white}>{children}</Typography>
              )
            }
          }}
        />
      </Box>
    );
  };

  return (
    <Box>
      <AnimatedView>
        <Box
          sx={{
            display: "flex",
            flexDirection: {
              xs: primaryProject?.reverseLayout ? "column-reverse" : "column",
              md: "row"
            },
            boxShadow: "0 10px 20px rgba(0,0,0,0.19), 0 6px 6px rgba(0,0,0,0.23)",
            borderRadius: "10px",
            position: "relative",
            zIndex: 1
          }}>
          {!primaryProject?.reverseLayout && <Image />}
          {primaryProject?.reverseLayout && <Highlight reversed />}
          <Box
            sx={{
              width: { xs: "100%", md: "65%" },
              py: { xs: 5, md: 10 },
              px: { xs: 5, md: 25 },
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "flex-start",
              backgroundColor: colors.white,
              gap: 5
            }}>
            <Typography variant="h4">{primaryProject?.title}</Typography>
            <RenderContent content={primaryProject?.descriptionShort?.json} />
            <Box
              sx={{
                width: "100%",
                display: "flex",
                justifyContent: { xs: "center", md: "flex-start" }
              }}>
              <NavButton
                sx={{ mt: 5 }}
                variant="primary"
                label={primaryProject?.callToAction as string}
                route={`${routes.project}/${primaryProject?.id}`}
              />
            </Box>
          </Box>
          {!primaryProject?.reverseLayout && <Highlight />}
          {primaryProject?.reverseLayout && <Image reversed />}
        </Box>
      </AnimatedView>
      {(secondaryProjectsCollection?.items?.length || 0) > 0 && (
        <AnimatedView delay={300} animation="fadeIn">
          <Grid container spacing={5} sx={{ px: { xs: 5, md: 20 }, pt: { xs: 5, md: 10 } }}>
            {solution?.secondaryProjectsCollection?.items.map((project, index) => (
              <Grid item xs={12} md={6} lg={4} key={generateId(5)}>
                <ProjectCard
                  connect={computeConnections(index)}
                  buttonVariant="outlined"
                  project={project as Project}
                />
              </Grid>
            ))}
          </Grid>
        </AnimatedView>
      )}
    </Box>
  );
};
